import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin } from 'lucide-react';
import { contactInfo, testimonials, aboutContent } from '../mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import WhatsAppCTA from '../components/WhatsAppCTA';
import FAQ from '../components/FAQ';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    honeypot: '' // Bot prevention field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Message Sent!', {
          description: data.message || 'Thank you for contacting us. We\'ll get back to you soon.',
        });
        setFormData({ name: '', email: '', message: '', honeypot: '' });
      } else {
        toast.error('Error', {
          description: data.detail || 'Failed to send message. Please try again.',
        });
      }
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to send message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Car Detailing Tralee | Kingdom Auto Detailing | From €60 | Kerry</title>
        <meta name="description" content="Professional car detailing in Tralee, Co Kerry. Ceramic coating, paint correction, interior & exterior detailing from €60. Serving Tralee, Listowel, Ardfert. Book today!" />
        <meta name="keywords" content="car detailing Tralee, car valeting Tralee, auto detailing Tralee, ceramic coating Tralee, paint correction Tralee, car wash Tralee, mobile valeting Tralee, car cleaning Tralee, professional car detailing Tralee, car valet Tralee, interior detailing Tralee, exterior detailing Tralee, best car detailing Tralee Kerry" />
        <link rel="canonical" href="https://kingdomautodetailing.ie/" />
        <meta name="geo.region" content="IE-KY" />
        <meta name="geo.placename" content="Tralee, Kerry" />
        <meta name="geo.position" content="52.2704;-9.7026" />
        <meta name="ICBM" content="52.2704, -9.7026" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-zinc-900 to-black py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-8">
            <img 
              src="/kingdom-logo.svg"
              alt="Kingdom Auto Detailing"
              className="h-64 md:h-80 w-auto mx-auto mb-6"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
            <span className="text-amber-500">KINGDOM</span>
            <br />
            <span className="text-white">AUTO DETAILING</span>
          </h1>
          <a
            href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
            className="inline-block text-4xl md:text-5xl font-bold text-white hover:text-amber-500 transition-colors mt-6"
          >
            {contactInfo.phone}
          </a>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Experience the Kingdom Auto Detailing difference
            </h2>
            <p className="text-xl text-gray-400">Professional detailing</p>
          </div>

          <div className="bg-zinc-900 rounded-2xl p-8 md:p-12 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">Our specialised services</h3>
            <p className="text-lg text-gray-300 leading-relaxed">
              Kingdom Auto Detailing offers a comprehensive range of services designed to meet all your automotive appearance needs. 
              From meticulous detailing to advanced paint correction and long-lasting ceramic coatings, we have you covered.
            </p>
          </div>

          {/* Service Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { title: 'Ceramic coating', desc: 'Protect your investment with our premium ceramic coating services, providing long-lasting protection and enhanced shine.' },
              { title: 'Paint correction', desc: 'Restore your vehicle\'s paint to its original glory with our expert paint correction techniques.' },
              { title: 'Detailing services', desc: 'From interior to exterior, our detailing services will leave your vehicle looking and feeling its best.' },
              { title: 'And more', desc: 'We are passionate about cars, and we use only the best products.' }
            ].map((service, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 hover:scale-105 transition-transform duration-300 cursor-pointer"
              >
                <h4 className="text-xl font-bold text-white mb-3">{service.title}</h4>
                <p className="text-blue-100 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 bg-zinc-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">{aboutContent.title}</h2>
            <p className="text-blue-100 leading-relaxed mb-6">{aboutContent.description}</p>
          </div>

          <div className="bg-black rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-white mb-4">{aboutContent.commitment.title}</h3>
            <p className="text-gray-300 leading-relaxed">{aboutContent.commitment.text}</p>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <WhatsAppCTA />

      {/* Contact Form */}
      <section className="py-16 px-4 bg-zinc-900">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-8">Contact us</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field - hidden from users */}
            <input
              type="text"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleChange}
              style={{ display: 'none' }}
              tabIndex="-1"
              autoComplete="off"
            />
            <div>
              <Input
                type="text"
                name="name"
                placeholder="Name *"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
              />
            </div>
            <div>
              <Input
                type="email"
                name="email"
                placeholder="Email address *"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 h-12"
              />
            </div>
            <div>
              <Textarea
                name="message"
                placeholder="Message *"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="bg-zinc-800 border-zinc-700 text-white placeholder-gray-500 resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold h-12 text-lg"
            >
              {isSubmitting ? 'Sending...' : 'Submit form'}
            </Button>
          </form>

          {/* Location Info */}
          <div className="mt-12 text-center">
            <h3 className="text-2xl font-bold text-white mb-6">Location</h3>
            <div className="space-y-2 text-gray-300">
              <p className="font-semibold">{contactInfo.address}</p>
              <p>{contactInfo.city}</p>
              <p>{contactInfo.state}</p>
              <p className="text-xl font-bold text-amber-500 mt-4">{contactInfo.phone}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <FAQ />
    </div>
  );
};

export default Home;